import { useEffect, useState, type ReactNode } from 'react';
import { NativeSelect } from '@chakra-ui/react';
import type { AppUpdateStatus, Bridge, UpdateChannel } from '@casa/bridge';
import { SettingsGroup, SettingsRow } from './settings_ui.tsx';

// "Updates", in the App settings (docs/specs/app-updates.md AU-28): the version the app runs and
// where it came from, a release that is ready with "Restart now", "Check now", the channel, and
// when a newer release needs the app from the store. Only in a shell with updates; nothing in a
// browser, which gets the app from its Tilez Hub.

const date = (iso: string) : string => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function checkDetail(status: AppUpdateStatus) : string {
    if (status.error) {
        return `The last check failed: ${status.error}`;
    }

    return status.lastCheck ? `Last checked ${date(status.lastCheck)}` : 'Not checked yet';
}

export function UpdatesGroup({ shell }: { shell: Bridge }) : ReactNode {
    const [status, setStatus] = useState<AppUpdateStatus>();
    const [isChecking, setChecking] = useState(false);

    useEffect(() => {
        shell.app.updateStatus().then(setStatus, () => setStatus(undefined));
    }, [shell]);

    if (!status) {
        return null;
    }

    const check = async () : Promise<void> => {
        setChecking(true);
        setStatus(await shell.app.checkForUpdates().catch(() => status));
        setChecking(false);
    };

    const setChannel = async (channel: UpdateChannel) : Promise<void> => {
        setStatus(await shell.app.setChannel(channel).catch(() => status));
    };

    const notes = [
        status.enabled ? undefined : 'This build does not update itself (a development build).',
        status.storeUpdate ? 'A newer Tilez needs an update of the app itself, from the App Store, Google Play or its installer.' : undefined,
    ].filter((note) => note !== undefined);

    return (
        <SettingsGroup title="Updates" note={notes.length > 0 ? notes.join(' ') : undefined}>
            <SettingsRow
                icon="circle-info"
                title="Version"
                detail={`${status.active.version}, ${status.active.source === 'downloaded' ? 'downloaded' : 'built in'}`}
                data-testid="updates-version"
            />
            {status.pending ? (
                <SettingsRow
                    icon="arrows-rotate"
                    title="Restart now"
                    detail={`${status.pending.version} is ready; it also starts by itself when no one uses the screen`}
                    color="#60a5fa"
                    opens={false}
                    onClick={() => void shell.app.applyUpdate().catch(() => undefined)}
                    data-testid="updates-restart"
                />
            ) : null}
            {status.enabled ? (
                <SettingsRow
                    icon="cloud-arrow-down"
                    title={isChecking ? 'Checking…' : 'Check now'}
                    detail={checkDetail(status)}
                    opens={false}
                    onClick={isChecking ? undefined : () => void check()}
                    data-testid="updates-check"
                />
            ) : null}
            <SettingsRow
                icon="flask"
                title="Channel"
                detail={status.channel === 'test' ? 'Test: releases before everyone gets them' : 'Stable'}
                end={
                    <NativeSelect.Root size="sm" width="auto">
                        <NativeSelect.Field aria-label="Update channel" value={status.channel} onChange={(event) => void setChannel(event.target.value as UpdateChannel)}>
                            <option value="stable">Stable</option>
                            <option value="test">Test</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                    </NativeSelect.Root>
                }
            />
        </SettingsGroup>
    );
}
